import { formatGold, formatPlayClock } from '../../format';
import {
  endlessWorldName,
  gateStatus,
  hasEarlyInvasionApi,
  hasGateApi,
  onGameEvent,
  openGate,
  playSeconds,
  startInvasionEarly,
} from '../../gameApi';
import { onAutoThrowChange, readAutoThrow, setAutoThrow } from '../../autoThrow';
import { onWakeLockChange, readWakeLock, setWakeLock, wakeLockSnapshot, type WakeLockSnapshot } from '../../wakeLock';
import { readRenderParams, writeRenderParams, type RenderParams } from '../../../game3d/renderParams';
import { maybeShowLore } from '../../lore';
import { readConsent, writeConsent } from '../../../telemetry/consent';
import { getTelemetry } from '../../../telemetry/api';
import { toast } from '../../toast';
import { mountTips } from '../../tips';
import type { ScreenCtx, ScreenMount } from '../../types';
import { renderDiceTab } from './dice';
import { renderShopTab } from './shop';
import { renderTreeTab } from './tree';
import { applyForgeScroll, closeForgeModal, renderForgeTab } from './forge';
import { openSaveTransfer } from './saveTransfer';
import { hasForge } from '../../abyssApi';
import { lastCombatLog } from '../../combatApi';
import { openLastRecapModal } from '../damageRecap';
import { onTreeRevealChange } from '../../treeReveal';
import { isDebugEnabled, openDevNotes } from '../devNotes';
import { mountUnlockCodeForm, unlockCodeFormHtml } from '../../unlockCodeForm';
import { IS_PREPROD } from '../../channel';
import { icon } from '../../icons';
import { overlayRoot } from '../../columnScale';
import { registerEscape } from '../../keyboard';

type TabId = 'tree' | 'shop' | 'dice' | 'forge';

/**
 * Le quatrième onglet, **Forge** (v0.46.0, A94), n'existe dans la barre que
 * lorsque `hasForge()` (Artisan des dés acquis) : sinon trois onglets comme
 * avant, sans aucun décalage.
 */
const TAB_LABEL: Record<TabId, string> = {
  tree: 'Arbre',
  shop: 'Boutique',
  dice: 'Dés',
  forge: 'Forge',
};

/** Jours d'Expédition d'un cycle avant l'Invasion (MVP_DESIGN §1). */
const DAYS_PER_LEVEL = 5;

export const mountAccalmie: ScreenMount = (container, ctx: ScreenCtx) => {
  const { game, battle } = ctx;
  let activeTab: TabId = 'tree';

  const el = document.createElement('div');
  el.className = 'screen screen--accalmie';
  // Charte F1 « Pierre taillée », lot 2 (v0.15.0) : jeton d'or, jour et niveau,
  // temps de jeu, barre de cycle en pierres taillées, onglets `.f-tab`, pied
  // avec le bouton « Jour n → ». Depuis la v0.38.1, les options « Lancer
  // automatique », « Garder l'écran allumé » (A60), « Particules magiques des
  // dés » (A78) et « Statistiques anonymes d'équilibrage » (phase 3) vivent
  // dans le menu **Réglages** ouvert par la molette de l'en-tête, avec les
  // accès « Écran titre », « Notes de dev » et « Debug » (mode debug).
  el.innerHTML = `
    <header class="accalmie-header">
      <div class="f-chip accalmie-gold">${icon('gold')}<b class="num" data-role="gold"></b></div>
      <div class="accalmie-head-right">
        <div class="accalmie-progress" data-role="progress"></div>
        <div class="accalmie-clock" data-role="play-clock" hidden></div>
      </div>
      <button type="button" class="f-ibtn accalmie-gear" data-action="settings" aria-label="Réglages" aria-haspopup="dialog" aria-expanded="false">${icon('gear')}</button>
    </header>
    <div class="accalmie-alert" data-role="telemetry-alert" role="alert" hidden></div>
    <div class="f-notice accalmie-trial" data-role="trial-banner" hidden></div>
    <div class="cycle-bar" data-role="cycle">
      <ol class="cycle-pips" data-role="cycle-pips" aria-label="Cycle en cours"></ol>
      <button type="button" class="f-btn f-btn--ghost cycle-early" data-action="early" hidden>Aller au devant</button>
    </div>
    <nav class="f-tabs accalmie-tabs" role="tablist" aria-label="Accalmie">
      <button type="button" class="f-tab" role="tab" data-tab="tree" aria-selected="false">${TAB_LABEL.tree}</button>
      <button type="button" class="f-tab" role="tab" data-tab="shop" aria-selected="false">${TAB_LABEL.shop}</button>
      <button type="button" class="f-tab" role="tab" data-tab="dice" aria-selected="false">${TAB_LABEL.dice}</button>
    </nav>
    <div class="accalmie-body" data-role="body" role="tabpanel"></div>
    <div class="accalmie-footer">
      <button type="button" class="f-btn f-btn--ghost f-btn--sm accalmie-last-recap" data-action="last-recap" hidden>Dernier rapport de combat</button>
      <div class="accalmie-footer-row">
        <button type="button" class="f-btn f-btn--primary accalmie-next" data-action="next"></button>
      </div>
    </div>
  `;
  container.appendChild(el);

  /**
   * Menu **Réglages** (v0.38.1) : petite fenêtre ouverte par la molette de
   * l'en-tête. Elle regroupe les quatre options du joueur et trois accès :
   * « Écran titre » (retour au début sans toucher à la partie), « Notes de
   * dev » et, en mode debug seulement, « Debug » (onglet Debug des Notes).
   * L'élément est construit une fois et **posé dans `#app` seulement le temps
   * de l'ouverture** (voile `.f-scrim` : les info-bulles se taisent pendant
   * ce temps, puis reviennent), comme les autres fenêtres de l'interface.
   */
  const menu = document.createElement('div');
  menu.className = 'f-scrim accalmie-menu-scrim';
  menu.setAttribute('data-role', 'settings-scrim');
  menu.innerHTML = `
    <div class="f-modal accalmie-menu" data-role="settings-menu" role="dialog" aria-modal="true" aria-labelledby="accalmie-menu-title">
      <div class="accalmie-menu-head">
        <h2 class="f-modal-title accalmie-menu-title" id="accalmie-menu-title">Réglages</h2>
        <button type="button" class="f-ibtn" data-action="settings-close" aria-label="Fermer">${icon('close')}</button>
      </div>
      <div class="accalmie-options">
        <label class="accalmie-option" data-role="auto-throw-option">
          <input type="checkbox" class="f-check accalmie-option-box" data-role="auto-throw">
          <span>Lancer automatique</span>
        </label>
        <label class="accalmie-option" data-role="wake-lock-option">
          <input type="checkbox" class="f-check accalmie-option-box" data-role="wake-lock">
          <span class="accalmie-option-text">
            <span>Garder l'écran allumé</span>
            <small class="accalmie-option-note" data-role="wake-lock-note" hidden></small>
          </span>
        </label>
        <label class="accalmie-option" data-role="dice-particles-option">
          <input type="checkbox" class="f-check accalmie-option-box" data-role="dice-particles">
          <span class="accalmie-option-text">
            <span>Particules magiques des dés</span>
            <small class="accalmie-option-note">À désactiver sur un téléphone lent</small>
          </span>
        </label>
        <label class="accalmie-option" data-role="telemetry-option">
          <input type="checkbox" class="f-check accalmie-option-box" data-role="telemetry">
          <span class="accalmie-option-text">
            <span>Statistiques anonymes d'équilibrage</span>
            <small class="accalmie-option-note">Envoie le déroulé de vos combats (dés, talents, dégâts), sans aucune donnée personnelle.</small>
            <small class="accalmie-option-status" data-role="telemetry-status"></small>
          </span>
        </label>
      </div>
      <p class="accalmie-entitlement" data-role="entitlement-state" hidden></p>
      <div class="accalmie-code" data-role="settings-code" hidden>
        <p class="accalmie-code-title">Vous avez un code de déblocage ?</p>
        ${unlockCodeFormHtml('settings')}
      </div>
      <div class="accalmie-menu-actions">
        <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="title">${icon('play')}<span>Écran titre</span></button>
        <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="save-transfer">${icon('shield')}<span>Sauvegarde</span></button>
        <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="dev">${icon('book')}<span>Notes de dev</span></button>
        <button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="debug"${isDebugEnabled() ? '' : ' hidden'}>Debug</button>
      </div>
    </div>
  `;

  const goldEl = el.querySelector<HTMLElement>('[data-role="gold"]');
  const gearBtn = el.querySelector<HTMLButtonElement>('[data-action="settings"]');
  const autoThrowBox = menu.querySelector<HTMLInputElement>('[data-role="auto-throw"]');
  const wakeLockBox = menu.querySelector<HTMLInputElement>('[data-role="wake-lock"]');
  const wakeLockNote = menu.querySelector<HTMLElement>('[data-role="wake-lock-note"]');
  const particlesBox = menu.querySelector<HTMLInputElement>('[data-role="dice-particles"]');
  const telemetryBox = menu.querySelector<HTMLInputElement>('[data-role="telemetry"]');
  const telemetryStatus = menu.querySelector<HTMLElement>('[data-role="telemetry-status"]');
  const telemetryAlert = el.querySelector<HTMLElement>('[data-role="telemetry-alert"]');
  const trialEl = el.querySelector<HTMLElement>('[data-role="trial-banner"]');
  const entitlementEl = menu.querySelector<HTMLElement>('[data-role="entitlement-state"]');
  // v0.55.3 : un code de déblocage se saisit aussi ici, à tout niveau (A112, retour du GD).
  const codeBox = menu.querySelector<HTMLElement>('[data-role="settings-code"]');
  const unmountCodeForm = codeBox ? mountUnlockCodeForm(codeBox, game, { onUnlocked: () => renderTrial() }) : () => {};
  const progressEl = el.querySelector<HTMLElement>('[data-role="progress"]');
  const clockEl = el.querySelector<HTMLElement>('[data-role="play-clock"]');
  const pipsEl = el.querySelector<HTMLElement>('[data-role="cycle-pips"]');
  const earlyBtn = el.querySelector<HTMLButtonElement>('[data-action="early"]');
  const bodyEl = el.querySelector<HTMLElement>('[data-role="body"]');
  const nextBtn = el.querySelector<HTMLButtonElement>('[data-action="next"]');
  const lastRecapBtn = el.querySelector<HTMLButtonElement>('[data-action="last-recap"]');
  const tabsNav = el.querySelector<HTMLElement>('.accalmie-tabs');
  /** Les onglets présents, dans l'ordre de la barre (le bouton Forge s'y ajoute et s'en retire). */
  const tabButtons = (): HTMLButtonElement[] => Array.from(el.querySelectorAll<HTMLButtonElement>('[data-tab]'));
  const forgeTabBtn = document.createElement('button');
  forgeTabBtn.type = 'button';
  forgeTabBtn.className = 'f-tab';
  forgeTabBtn.setAttribute('role', 'tab');
  forgeTabBtn.setAttribute('data-tab', 'forge');
  forgeTabBtn.setAttribute('aria-selected', 'false');
  forgeTabBtn.textContent = TAB_LABEL.forge;

  // ─────────────────────────────────────────────────────────── Menu Réglages

  let unregisterMenuEscape = (): void => {};
  let menuOpen = false;
  function closeMenu(): void {
    if (!menuOpen) return;
    menuOpen = false;
    unregisterMenuEscape();
    unregisterMenuEscape = () => {};
    menu.remove();
    gearBtn?.setAttribute('aria-expanded', 'false');
  }
  function openMenu(): void {
    if (menuOpen) return;
    menuOpen = true;
    renderFooter();
    overlayRoot().appendChild(menu);
    unregisterMenuEscape = registerEscape(menu, closeMenu);
    gearBtn?.setAttribute('aria-expanded', 'true');
    menu.querySelector<HTMLButtonElement>('[data-action="settings-close"]')?.focus();
  }
  const onGear = (): void => openMenu();
  gearBtn?.addEventListener('click', onGear);
  const onMenuScrim = (e: Event): void => {
    if (e.target === menu) closeMenu();
  };
  menu.addEventListener('click', onMenuScrim);
  menu.querySelector('[data-action="settings-close"]')?.addEventListener('click', closeMenu);
  // « Écran titre » : retour au début sans changer la phase du jeu — le titre
  // propose « Continuer », qui ramène ici (`ctx.navigate`, comme au lancement).
  menu.querySelector('[data-action="title"]')?.addEventListener('click', () => {
    closeMenu();
    ctx.navigate('title');
  });
  menu.querySelector('[data-action="save-transfer"]')?.addEventListener('click', () => {
    closeMenu();
    openSaveTransfer(ctx);
  });
  menu.querySelector('[data-action="dev"]')?.addEventListener('click', () => {
    closeMenu();
    openDevNotes(ctx);
  });
  menu.querySelector('[data-action="debug"]')?.addEventListener('click', () => {
    closeMenu();
    openDevNotes(ctx, 'debug');
  });

  /**
   * Chrono de partie (COMMENTS v0.6.0 §2) : le même compteur qu'en combat, dans
   * l'en-tête de l'Accalmie. Il avance ici grâce à `game.addMenuTime()`, poussé
   * par le routeur d'écrans tant qu'un écran hors combat est affiché
   * (`src/ui/app.ts`) ; cet écran ne fait que le relire chaque seconde.
   */
  function renderClock(): void {
    if (!clockEl) return;
    const seconds = playSeconds(game);
    clockEl.hidden = seconds === null;
    if (seconds === null) return;
    clockEl.textContent = `Temps de jeu ${formatPlayClock(seconds)}`;
  }

  function renderHeader(): void {
    const s = game.state;
    if (goldEl) goldEl.textContent = formatGold(s.gold);
    renderClock();
    if (progressEl) {
      // Mode infini (phase 5, A82) : le nom du monde tiré suit le niveau.
      const world = endlessWorldName(game);
      progressEl.textContent = `${s.invasionReady ? 'Invasion prête' : `Jour ${s.day}/5`} · Niveau ${s.level}${world ? ` · ${world}` : ''}`;
    }
  }

  /**
   * Barre de cycle — spec §11.1.4 : 5 pastilles de jour + le marqueur
   * d'Invasion, la position courante mise en avant, et le bouton « Aller au
   * devant » qui déclenche l'Invasion sans jouer les jours restants.
   */
  function renderCycle(): void {
    const s = game.state;
    if (pipsEl) {
      pipsEl.innerHTML = '';
      for (let day = 1; day <= DAYS_PER_LEVEL; day++) {
        const pip = document.createElement('li');
        const done = s.invasionReady || day < s.day;
        const current = !s.invasionReady && day === s.day;
        pip.className = `cycle-pip${done ? ' cycle-pip--done' : ''}${current ? ' cycle-pip--current' : ''}`;
        pip.setAttribute('data-day', String(day));
        pip.textContent = String(day);
        if (current) pip.setAttribute('aria-current', 'step');
        pipsEl.appendChild(pip);
      }
      const invasion = document.createElement('li');
      invasion.className = `cycle-pip cycle-pip--invasion${s.invasionReady ? ' cycle-pip--current' : ''}`;
      invasion.setAttribute('data-day', 'invasion');
      invasion.title = 'Invasion';
      // Libellé texte plutôt qu'un pictogramme : les polices du jeu n'ont pas
      // le pictogramme des épées croisées (caractère de remplacement sur mobile).
      invasion.textContent = 'Inv.';
      if (s.invasionReady) invasion.setAttribute('aria-current', 'step');
      pipsEl.appendChild(invasion);
    }
    if (earlyBtn) {
      // Inutile quand l'Invasion est déjà la prochaine sortie ; masqué tant que
      // le cœur n'expose pas `startInvasionEarly()`.
      earlyBtn.hidden = s.invasionReady || !hasEarlyInvasionApi(game);
    }
  }

  /**
   * État du dernier envoi de télémétrie sous la case (A85, v0.40.4) : réussi,
   * non tenté (et pourquoi), ou **en échec avec la raison du Worker**, en rouge.
   * Lu à chaque rendu du pied et à l'ouverture du menu ; rien si le moteur n'est
   * pas encore installé (il l'est 250 ms après le montage).
   */
  function renderTelemetryStatus(): void {
    if (!telemetryStatus) return;
    const st = getTelemetry()?.state() ?? null;
    if (!st || !readConsent()) {
      telemetryStatus.textContent = '';
      telemetryStatus.removeAttribute('data-tone');
      telemetryStatus.hidden = true;
      return;
    }
    telemetryStatus.hidden = false;
    const when = st.lastAttemptAt ? ` à ${st.lastAttemptAt.slice(11, 16)} UTC` : '';
    const r = st.lastResult;
    let text: string;
    let tone: 'danger' | 'good' | null = null;
    if (r === null) text = `Aucun envoi pour l'instant · ${st.queued} en attente`;
    else if (r === 'ok') {
      text = `Dernier envoi réussi${when} · ${st.sent} envoyé${st.sent > 1 ? 's' : ''}${st.queued > 0 ? ` · ${st.queued} en attente` : ''}`;
      tone = 'good';
    } else if (r.startsWith('error')) {
      text = `Dernier envoi en échec${when} : ${r}${st.lastDetail ? ` — ${st.lastDetail}` : ''}${st.rejected > 0 ? ` · ${st.rejected} rejeté${st.rejected > 1 ? 's' : ''}` : ''}`;
      tone = 'danger';
    } else {
      const why: Record<string, string> = { offline: 'hors ligne', noUrl: 'aucune adresse configurée', inCombat: 'en combat', disabled: 'désactivée' };
      text = `Dernier envoi non tenté${when} (${why[r] ?? r}) · ${st.queued} en attente`;
    }
    telemetryStatus.textContent = text;
    if (tone) telemetryStatus.setAttribute('data-tone', tone);
    else telemetryStatus.removeAttribute('data-tone');
  }

  /**
   * Bandeau d'alerte sous l'en-tête (A85, v0.40.5) : tant que le **dernier
   * envoi** de télémétrie est en échec, l'écran principal le dit en rouge avec
   * la raison du Worker, sans ouvrir un menu. Préprod et mode debug seulement
   * (production : A80, sans annonce). Disparaît au premier envoi réussi.
   */
  function renderTelemetryAlert(): void {
    if (!telemetryAlert) return;
    const st = IS_PREPROD || isDebugEnabled() ? (getTelemetry()?.state() ?? null) : null;
    const r = st?.lastResult ?? '';
    if (!st || !readConsent() || !r.startsWith('error')) {
      telemetryAlert.hidden = true;
      telemetryAlert.replaceChildren();
      return;
    }
    const when = st.lastAttemptAt ? ` à ${st.lastAttemptAt.slice(11, 16)} UTC` : '';
    // `error:network` : le navigateur n'a pas pu joindre le Worker (connexion,
    // DNS ou bloqueur, préflight CORS refusé) ; `error:timeout` : pas de
    // réponse en 8 s. Les autres codes viennent du Worker lui-même.
    const hint =
      r === 'error:network'
        ? ' — Worker injoignable : connexion coupée, adresse bloquée, ou origine absente d’ALLOWED_ORIGINS'
        : r === 'error:timeout'
          ? ' — pas de réponse du Worker en 8 s'
          : '';
    telemetryAlert.replaceChildren();
    const text = document.createElement('span');
    text.className = 'accalmie-alert-text';
    text.textContent = `Télémétrie en échec${when} : ${r}${st.lastDetail ? ` — ${st.lastDetail}` : ''}${hint}${st.rejected > 0 ? ` · ${st.rejected} rejeté${st.rejected > 1 ? 's' : ''}` : ''} · ${st.queued} en attente`;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'f-btn f-btn--ghost f-btn--sm accalmie-alert-retry';
    retry.setAttribute('data-action', 'telemetry-retry');
    retry.textContent = st.sending ? 'Envoi…' : 'Réessayer l’envoi';
    retry.disabled = st.sending || st.queued === 0;
    retry.addEventListener('click', () => {
      retry.disabled = true;
      retry.textContent = 'Envoi…';
      void getTelemetry()
        ?.flush()
        .finally(() => {
          renderTelemetryAlert();
          renderTelemetryStatus();
        });
    });
    telemetryAlert.append(text, retry);
    telemetryAlert.hidden = false;
  }

  /**
   * Bandeau d'essai (A112) : tant que le joueur est bloqué par le mur, un rappel
   * sous l'en-tête et le bouton « Débloquer » qui ouvre l'écran d'achat. Rien
   * quand le jeu est acheté, ni sans l'API du cœur (avant la fusion du lot A112).
   * La ligne du menu Réglages dit l'état de l'appareil.
   */
  function renderTrial(): void {
    const st = gateStatus(game);
    if (trialEl) {
      if (!st || !st.locked) {
        trialEl.hidden = true;
        trialEl.replaceChildren();
      } else {
        trialEl.hidden = false;
        trialEl.toggleAttribute('data-reached', st.reached);
        trialEl.innerHTML = `${icon('lock')}<span class="accalmie-trial-text">${
          st.reached
            ? `Niveau ${st.freeLevels + 1} gagné\u00a0! Débloquez le jeu pour continuer.`
            : `Version d\u2019essai \u00b7 niveaux 1 et ${st.freeLevels}`
        }</span><button type="button" class="f-btn f-btn--sm ${st.reached ? 'f-btn--buy' : 'f-btn--primary'}" data-action="unlock">Débloquer</button>`;
      }
    }
    if (entitlementEl) {
      entitlementEl.hidden = !st;
      if (st) entitlementEl.innerHTML = `Version\u00a0: <b>${st.entitled ? 'Jeu complet' : 'Version d\u2019essai'}</b>`;
    }
    if (codeBox) codeBox.hidden = !st || st.entitled;
  }

  function renderFooter(): void {
    const s = game.state;
    // Option « Lancer automatique » (COMMENTS v0.4.1 §2), permanente jusqu'à
    // désactivation : elle vit dans le navigateur, pas dans la sauvegarde.
    if (autoThrowBox) autoThrowBox.checked = readAutoThrow();
    renderWakeLock(wakeLockSnapshot());
    if (particlesBox) particlesBox.checked = readDiceParticles();
    // Statistiques anonymes (phase 3, A78 / A80) : cochée par défaut.
    if (telemetryBox) telemetryBox.checked = readConsent();
    renderTelemetryStatus();
    // A122-11 : le dernier rapport de combat, absent tant qu'aucun combat n'a eu lieu dans la session.
    if (lastRecapBtn) lastRecapBtn.hidden = lastCombatLog(game) === null;
    if (nextBtn) {
      // La flèche est dessinée (icône) : le libellé lu reste « Jour n → ».
      nextBtn.innerHTML = s.invasionReady
        ? 'Invasion !'
        : `<span>Jour ${s.day}</span>${icon('arrow')}<span class="sr-only"> →</span>`;
    }
  }

  /**
   * Option « Garder l'écran allumé » (A60). Sans l'API (Firefox < 126,
   * Safari < 16.4, page non sécurisée), la case est grisée et décochée avec
   * une mention ; un refus ponctuel du navigateur garde la case utilisable.
   */
  function renderWakeLock(s: WakeLockSnapshot): void {
    if (wakeLockBox) {
      wakeLockBox.disabled = !s.supported;
      wakeLockBox.checked = s.supported && readWakeLock();
    }
    if (wakeLockNote) {
      const note = !s.supported
        ? 'indisponible sur ce navigateur'
        : s.status === 'unavailable'
          ? 'refusé par le navigateur'
          : '';
      wakeLockNote.hidden = note === '';
      wakeLockNote.textContent = note;
    }
  }

  /**
   * Option « Particules magiques des dés » (v0.35.0, A78) : la clé
   * `diceParticles` des réglages de rendu, lue et écrite **par la vue 3D**
   * quand elle est là (elle applique sans rechargement et persiste), sinon
   * directement dans la préférence (vue de secours).
   */
  const renderView = battle as unknown as Partial<{
    getRenderParams: () => RenderParams;
    setRenderParams: (patch: Partial<RenderParams>) => RenderParams;
  }>;
  function readDiceParticles(): boolean {
    if (typeof renderView.getRenderParams === 'function') return renderView.getRenderParams().diceParticles;
    return readRenderParams().diceParticles;
  }
  function writeDiceParticles(on: boolean): boolean {
    if (typeof renderView.setRenderParams === 'function') {
      return renderView.setRenderParams({ diceParticles: on }).diceParticles;
    }
    writeRenderParams({ ...readRenderParams(), diceParticles: on });
    return on;
  }

  function renderTabs(): void {
    // L'onglet Forge apparaît avec l'Artisan des dés (A94), jamais avant.
    const forgeOpen = hasForge(game);
    if (forgeOpen && !forgeTabBtn.isConnected) tabsNav?.appendChild(forgeTabBtn);
    else if (!forgeOpen && forgeTabBtn.isConnected) forgeTabBtn.remove();
    if (!forgeOpen && activeTab === 'forge') activeTab = 'tree';
    for (const btn of tabButtons()) {
      const on = btn.dataset.tab === activeTab;
      btn.setAttribute('aria-selected', String(on));
      btn.tabIndex = on ? 0 : -1;
    }
  }

  /** Défilement de la Boutique et des Dés, gardé d'un rafraîchissement à l'autre. */
  const bodyScroll: Partial<Record<TabId, number>> = {};
  let renderedTab: TabId | null = null;

  function renderBody(): void {
    if (!bodyEl) return;
    if (renderedTab && renderedTab !== 'tree') bodyScroll[renderedTab] = bodyEl.scrollTop;
    bodyEl.innerHTML = '';
    // L'arbre gère son propre défilement (graphe) et son panneau fixe en bas :
    // le corps ne défile pas pour lui.
    bodyEl.classList.toggle('accalmie-body--tree', activeTab === 'tree');
    bodyEl.setAttribute('data-tab-panel', activeTab);
    if (activeTab === 'tree') renderTreeTab(bodyEl, ctx);
    else if (activeTab === 'shop') renderShopTab(bodyEl, ctx, renderAll);
    else if (activeTab === 'forge') renderForgeTab(bodyEl, ctx, renderAll);
    else renderDiceTab(bodyEl, ctx, renderAll);
    bodyEl.scrollTop = activeTab === 'tree' ? 0 : (bodyScroll[activeTab] ?? 0);
    // Forge (A122-14) : la vue se cale sur ce que le joueur vient de toucher, au lieu de sauter en bas.
    if (activeTab === 'forge') applyForgeScroll(bodyEl);
    renderedTab = activeTab;
  }

  function renderAll(): void {
    renderHeader();
    renderCycle();
    renderFooter();
    renderTelemetryAlert();
    renderTrial();
    renderTabs();
    renderBody();
  }

  // Résultat d'un envoi pendant que l'Accalmie est affichée (`main.ts`) : bandeau et ligne du menu à jour.
  const onTelemetryResult = (): void => {
    renderTelemetryAlert();
    renderTelemetryStatus();
  };
  document.addEventListener('dod:telemetry-result', onTelemetryResult);

  const onTabClick = (e: Event): void => {
    const target = (e.currentTarget as HTMLButtonElement).dataset.tab as TabId | undefined;
    if (!target || target === activeTab) return;
    if (renderedTab && renderedTab !== 'tree' && bodyEl) bodyScroll[renderedTab] = bodyEl.scrollTop;
    // Changer d'onglet repart du haut ; un rafraîchissement garde la position.
    bodyScroll[target] = 0;
    activeTab = target;
    renderTabs();
    renderBody();
  };
  for (const btn of tabButtons()) btn.addEventListener('click', onTabClick);
  forgeTabBtn.addEventListener('click', onTabClick);
  // Onglets au clavier (motif ARIA « tablist ») : flèches gauche / droite,
  // Début / Fin ; seul l'onglet actif est dans l'ordre de tabulation.
  const onTabKey = (e: KeyboardEvent): void => {
    const buttons = tabButtons();
    const i = buttons.indexOf(e.currentTarget as HTMLButtonElement);
    if (i < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % buttons.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + buttons.length) % buttons.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = buttons.length - 1;
    if (next < 0) return;
    e.preventDefault();
    buttons[next].focus();
    buttons[next].click();
  };
  for (const btn of tabButtons()) btn.addEventListener('keydown', onTabKey);
  forgeTabBtn.addEventListener('keydown', onTabKey);

  const onAutoThrow = (): void => {
    setAutoThrow(battle, autoThrowBox?.checked === true);
  };
  autoThrowBox?.addEventListener('change', onAutoThrow);
  // La case du panneau de debug règle la même option : on se resynchronise.
  const offAutoThrow = onAutoThrowChange((on) => {
    if (autoThrowBox) autoThrowBox.checked = on;
  });

  const onWakeLock = (): void => {
    renderWakeLock(setWakeLock(wakeLockBox?.checked === true));
  };
  wakeLockBox?.addEventListener('change', onWakeLock);
  const offWakeLock = onWakeLockChange(renderWakeLock);

  const onParticles = (): void => {
    const on = writeDiceParticles(particlesBox?.checked === true);
    if (particlesBox) particlesBox.checked = on;
  };
  particlesBox?.addEventListener('change', onParticles);

  const onTelemetry = (): void => {
    writeConsent(telemetryBox?.checked === true);
  };
  telemetryBox?.addEventListener('change', onTelemetry);

  const onTrialClick = (e: Event): void => {
    if ((e.target as HTMLElement | null)?.closest('[data-action="unlock"]') && hasGateApi(game)) openGate(game);
  };
  trialEl?.addEventListener('click', onTrialClick);

  const onNext = (): void => {
    if (game.state.invasionReady) {
      game.startInvasion();
      return;
    }
    // Par sécurité (DECISIONS §G A33) : si le lore n'a jamais été vu (ex.
    // sauvegarde reprise avant l'écran titre), il précède aussi le tout
    // premier départ d'Expédition déclenché depuis l'Accalmie.
    maybeShowLore(ctx, () => game.startNextDay());
  };
  nextBtn?.addEventListener('click', onNext);
  const onLastRecap = (): void => {
    openLastRecapModal(game);
  };
  lastRecapBtn?.addEventListener('click', onLastRecap);

  const onEarly = (): void => {
    const remaining = Math.max(0, DAYS_PER_LEVEL - game.state.day + 1);
    const confirmed = window.confirm(
      `Aller au devant de l'Invasion ? ${remaining} jour${remaining > 1 ? 's' : ''} d'Expédition ${
        remaining > 1 ? 'sont sautés' : 'est sauté'
      }.`,
    );
    if (!confirmed) return;
    if (!startInvasionEarly(game)) toast('Invasion indisponible pour le moment.');
  };
  earlyBtn?.addEventListener('click', onEarly);

  // QA : un changement d'or change aussi ce qui est *achetable* (boutons de
  // lots, coûts de nœuds, aperçu du Recycleur) — on refait tout l'écran, pas
  // seulement l'en-tête, sinon un recyclage laissait des boutons grisés à tort.
  const offGold = game.on('gold', () => renderAll());
  const offPurchase = game.on('purchase', () => renderAll());
  // Contenant (événement détecté à l'exécution) : section Gobelet de l'onglet Dés.
  const offContainer = onGameEvent(game, 'container', () => renderAll());
  // Forge (A94) : une recette appliquée ou résolue redessine l'onglet, les cartes et l'or.
  const offForge = onGameEvent(game, 'forge', () => renderAll());
  // Mur payant (A112) : le bandeau d'essai suit l'achat (`entitlement`) et le boss du niveau 2 (`gate`).
  const offGate = onGameEvent(game, 'gate', () => renderTrial());
  const offEntitlement = onGameEvent(game, 'entitlement', () => renderTrial());

  // Case de debug « Tout l'arbre » (A98) : l'arbre se redessine sans toucher au reste.
  const offTreeReveal = onTreeRevealChange(() => {
    if (activeTab === 'tree') renderBody();
  });

  const clockTimer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    renderClock();
  }, 1000);

  renderAll();

  // Info-bulles de première partie : Arbre, Boutique, Dés, Invasion, « Aller au devant »
  // (phase 4, `ui/tips.ts`). En dernier : les ancres doivent exister.
  const offTips = mountTips(ctx, 'accalmie', el);

  return () => {
    offTips();
    unmountCodeForm();
    closeMenu();
    gearBtn?.removeEventListener('click', onGear);
    menu.removeEventListener('click', onMenuScrim);
    window.clearInterval(clockTimer);
    for (const btn of [...tabButtons(), forgeTabBtn]) {
      btn.removeEventListener('click', onTabClick);
      btn.removeEventListener('keydown', onTabKey);
    }
    closeForgeModal();
    nextBtn?.removeEventListener('click', onNext);
    lastRecapBtn?.removeEventListener('click', onLastRecap);
    earlyBtn?.removeEventListener('click', onEarly);
    autoThrowBox?.removeEventListener('change', onAutoThrow);
    offAutoThrow();
    wakeLockBox?.removeEventListener('change', onWakeLock);
    offWakeLock();
    particlesBox?.removeEventListener('change', onParticles);
    telemetryBox?.removeEventListener('change', onTelemetry);
    document.removeEventListener('dod:telemetry-result', onTelemetryResult);
    offGold();
    offPurchase();
    offContainer();
    offForge();
    offGate();
    offEntitlement();
    trialEl?.removeEventListener('click', onTrialClick);
    offTreeReveal();
  };
};
